import AuthPageFrame from '../AuthPageFrame';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff } from 'lucide-react';
import { FormEvent, useRef, useState } from 'react';
import { getErrorMessage } from '@/shared/lib/getErrorMessage';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { reachGoal } from '@/shared/lib/yandexMetrika';
import { toast } from '@/components/ui/sonner';
import { useAuthStore } from '../../model/authStore';
import { useMutation } from '@tanstack/react-query';

const LoginForm = () => {
  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [touched, setTouched] = useState<{ login: boolean; password: boolean }>({
    login: false,
    password: false
  });

  const location = useLocation();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const hasTrackedGoalRef = useRef(false);
  const loginError = !loginValue.trim() ? 'Введите электронную почту' : '';
  const passwordError = !password
    ? 'Введите пароль'
    : password.length < 6 ? 'Пароль должен содержать не менее 6 символов' : '';
  const canSubmit = !loginError && !passwordError;

  const {
    isPending: isPendingLogin,
    mutateAsync: mutateAsyncLogin
  } = useMutation({
    mutationFn: login,
    mutationKey: ['login', location.pathname],
    onError: (error) => {
      toast.error('Ошибка', {
        description: getErrorMessage(error, 'Ошибка авторизации')
      });
    },
    onSuccess: () => {
      if (!hasTrackedGoalRef.current) {
        hasTrackedGoalRef.current = true;
        reachGoal('vhod_v_lk');
      }
      toast.success('Вход выполнен', {
        description: 'Добро пожаловать в White Line.'
      });
      const from = (location.state as { from?: string } | null)?.from ?? '/';
      navigate(from, { replace: true });
    }
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isPendingLogin) return;

    setSubmitAttempted(true);
    if (!canSubmit) return;

    await mutateAsyncLogin({
      login: loginValue.trim(),
      password
    }).catch(() => undefined);
  };

  return (
    <AuthPageFrame subtitle="Вход в систему">
      <form
        className="space-y-4"
        noValidate
        onSubmit={handleSubmit}
      >
        <div className="space-y-2">
          <Label htmlFor="login">Электронная почта</Label>
          <Input
            autoComplete="username"
            disabled={isPendingLogin}
            error={(touched.login || submitAttempted) && !!loginError}
            id="login"
            onBlur={() => setTouched((prev) => ({ ...prev, login: true }))}
            onChange={(e) => setLoginValue(e.target.value)}
            placeholder="ivan@example.com"
            required
            type="text"
            value={loginValue}
          />
          {(touched.login || submitAttempted) && loginError ? (
            <p className="px-3 text-xs text-[hsl(var(--error-text))]">{loginError}</p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Пароль</Label>
          <Input
            autoComplete="current-password"
            disabled={isPendingLogin}
            error={(touched.password || submitAttempted) && !!passwordError}
            id="password"
            onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Введите пароль"
            required
            rightIcon={
              <button
                className="flex size-5 items-center justify-center text-muted-foreground-soft transition-colors hover:text-foreground"
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                onClick={() => setShowPassword((v) => !v)}
                type="button"
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            }
            type={showPassword ? 'text' : 'password'}
            value={password}
          />
          {(touched.password || submitAttempted) && passwordError ? (
            <p className="px-3 text-xs text-[hsl(var(--error-text))]">{passwordError}</p>
          ) : null}
        </div>

        <div className="flex justify-end">
          <Button asChild variant="tertiary">
            <Link to="/forgot-password">Забыли пароль?</Link>
          </Button>
        </div>

        <Button
          className="w-full"
          disabled={isPendingLogin}
          loading={isPendingLogin}
          size="lg"
          type="submit"
        >
          {isPendingLogin ? 'Входим...' : 'Войти'}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          Нет аккаунта?{' '}
          <Button asChild variant="tertiary">
            <Link to="/register">Зарегистрироваться</Link>
          </Button>
        </p>
      </form>
    </AuthPageFrame>
  );
};

export default LoginForm;